/* Copyright (c) 2025 MOCA Systems, Inc. All rights reserved. */

import { Observable } from 'rxjs';
import { FancyRef } from './FancyRef';
import { IRefLike, ISnapshotLike } from './IRefLike';

// add more properties as needed...
export interface IFancyServerValue {
	TIMESTAMP: object;
}

export const fancyRefDefaultDatabase: string = 'DEFAULT';

//TODO's
// - @rushstack/no-new-null - "null is deprecated, except for describing legacy api's"
//   - ...this is a legacy api, is there a better way to tell rush

/**
 * Base class for the abstraction of the firebase/database sdk. Replacing the firebase
 * reference with our own [[FancyRef]]. This class is the main configuration point for
 * swapping out all the direct database communication. See [[FancyAdminDatabase]] for the
 * admin sdk, [[FancyClientDatabase]] for the client sdk, and this is probably the place
 * where we could try to slap in tests against some entirely different database.
 * Initial focus is the ref, but can eventually be extended to every other rtdb method
 */
export abstract class FancyDatabase<SdkRef extends IRefLike> {
	public abstract readonly ServerValue: IFancyServerValue;

	/** fill in for firebase.database().ref(path) */
	protected abstract getSdkReference(path: string, partition?: string): SdkRef;
	public abstract set(ref: FancyRef, data: unknown): Promise<void>;
	public abstract update(ref: FancyRef, data: unknown): Promise<void>;
	public abstract child(ref: FancyRef, path: string): FancyRef;
	public abstract get<T = unknown>(ref: FancyRef): Promise<ISnapshotLike<T>>;
	public abstract generateChildKey(ref: FancyRef): string;
	public abstract getIndexedChildValue<T = unknown>(
		ref: FancyRef,
		childKey: string,
		childValue: string
	): Promise<ISnapshotLike<T>>;
	public abstract object$<T>(ref: FancyRef): Observable<ISnapshotLike<T>>;

	/**
	 * Get's a fancy reference
	 * loosely equivalant to firebase.database().ref(path).
	 * Generally not inteneded to be used directly but through fancy referencer.
	 */
	public getRef(path: string, partition: string | undefined): FancyRef {
		return {
			path,
			partition,
			// BEGIN PLACEHOLDER AS PART OF TP-6697
			// this should be a static value, registered once and correspond to the name
			// of the gcloud/ firebase project
			// consider doing the firebase thing and calling it the default until we actually
			// need to support more than one
			project: fancyRefDefaultDatabase,
			// END PLACEHOLDER AS PART OF TP-6697
		};
	}
	/**
	 * Constructs a firebase reference from a fancy reference.
	 * To be used right before a direct firebase sdk call.
	 * */
	public toFirebaseRef(fancyRef: FancyRef): SdkRef {
		return this.getSdkReference(fancyRef.path, fancyRef.partition);
	}

	/**
	 * convert "normal reference" to a [[FancyRef]].
	 * Probably only a temporary thing until modular is added
	 * @param sdkRef - some flavor of firebase ref
	 * @param databaseName - database name override
	 * @returns
	 */
	public toFancyRef(sdkRef: SdkRef, databaseName?: string): FancyRef {
		const path = refToPath(sdkRef, true);

		//modular ref does not contain _any_ reference to the database
		//that's going to be very annoying when doing the compact -> modular migration
		// (also probably not worth a temp interface to avoid the any)

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let sdkDatabaseName: string | undefined = (sdkRef as any)?.database?.app?.name;
		// seems like at least one of the sdks report their name as [DEFAULT], but doesn't
		// consider that to mean the same as undefined
		// TODO - either eliminate this "helper" entirely or move it to the subclasses where it can be more sane
		if (sdkDatabaseName === '[DEFAULT]') {
			sdkDatabaseName = undefined;
		}

		const partition: string | undefined = databaseName || sdkDatabaseName || undefined;
		return this.getRef(path, partition);
	}
}

// BEGIN PLACEHOLDER AS PART OF TP-6697
// Temporarily duplicated from FancyReferencer, deferring changes until the modular migration
// might just... age out
// alternatively, consider replacing with ref.toString() + refFromUrl()
/**
 * Convert a reference back into a string path. Optionally including the refUrl (firebaseGetters does not include this)
 * Opting for this instead of directly exposing a path getter to discourge path use without a damn good reason.
 * Only known good reason at the moment is cloud storage
 * TODO - add unit tests in https://mocasystems.atlassian.net/browse/TP-2741
 */
function refToPath(ref: IRefLike, includeRefUrl: boolean = false): string {
	const pathArray: string[] = [];
	if (ref.key) {
		pathArray.unshift(ref.key);
	}

	while (ref.parent?.key) {
		pathArray.unshift(ref.parent.key);
		ref = ref.parent;
	}

	if (!includeRefUrl) {
		pathArray.splice(0, 1);
	}

	//start with a slash for consistency with older stuff (firebase no care)
	return '/' + pathArray.join('/');
}
// END PLACEHOLDER AS PART OF TP-6697
